import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import RegistrationSuccess from "@/pages/RegistrationSuccess";
import Welcome from "@/pages/Welcome";
import Dashboard from "@/pages/Dashboard";
import RequisitionBuilder from "@/pages/RequisitionBuilder";
import RequisitionDetail from "@/pages/RequisitionDetail";
import MarketAnalysis from "@/pages/MarketAnalysis";
import CompetitiveIntelligence from "@/pages/CompetitiveIntelligence";
import TryRequisition from "@/pages/TryRequisition";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ReqLibrary from "@/pages/ReqLibrary";
import LibraryItemDetail from "@/pages/LibraryItemDetail";
import Settings from "@/pages/Settings";
import FAQ from "@/pages/FAQ";
import UserGuide from "@/pages/UserGuide";
import Sitemap from "@/pages/Sitemap";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Public routes that should be tracked by Google Analytics
const PUBLIC_ROUTES = [
  '/',
  '/try',
  '/pricing',
  '/faq',
  '/guide',
  '/sitemap',
  '/terms-of-service',
  '/privacy-policy',
  '/registration-success',
  '/welcome'
];

// Component to handle GA tracking on route changes
const GoogleAnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Only track if on a public route
    if (PUBLIC_ROUTES.includes(location.pathname)) {
      // Check if gtag is available
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href,
          page_path: location.pathname
        });
      }
    }
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GoogleAnalyticsTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/try" element={<TryRequisition />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/builder"
            element={
              <ProtectedRoute>
                <RequisitionBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requisition/:id"
            element={
              <ProtectedRoute>
                <RequisitionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requisition/:id/market-analysis"
            element={
              <ProtectedRoute>
                <MarketAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requisition/:id/competitive-intelligence"
            element={
              <ProtectedRoute>
                <CompetitiveIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <ReqLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/:id"
            element={
              <ProtectedRoute>
                <LibraryItemDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/guide" element={<UserGuide />} />
          <Route path="/sitemap" element={<Sitemap />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;